/**
 * Filiale context store for NeoTIM
 * Uses Zustand with immer + persist middleware
 *
 * Holds the user's filiale selection (mode + codes + dept path) that drives
 * the X-Context-Filiales / X-Context-Dept headers injected by the axios
 * interceptor (lib/api/client.ts).
 *
 * This store does NOT hold accessible filiales — those are a ceiling and live
 * in usePermissionStore.context.accessible_filiales. Only the user selection
 * lives here.
 */

import { STORAGE_KEYS } from "@lib/config/storageKeys";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

/**
 * Selection mode:
 * - "own"   → no header injected, backend uses the user's own filiale
 * - "multi" → X-Context-Filiales with selectedFilialeCodes
 * - "all"   → X-Context-Filiales with the full accessible_filiales list
 */
export type FilialeContextMode = "own" | "multi" | "all";

/**
 * Store interface
 */
export interface FilialeContextStore {
  // State
  /** Selection mode */
  mode: FilialeContextMode;
  /** Selected filiale codes (used in multi mode) */
  selectedFilialeCodes: string[];
  /** Selected department ltree path (optional) */
  selectedDeptPath: string | null;

  // Actions
  /** Set selection mode; clears codes when switching to own/all */
  setMode: (mode: FilialeContextMode) => void;
  /** Set selected codes; switches mode to multi when non-empty, own when empty */
  setSelectedFilialeCodes: (codes: string[]) => void;
  /** Set department ltree path (null clears it) */
  setSelectedDeptPath: (path: string | null) => void;
  /** Reset to initial state (logout) */
  reset: () => void;
}

/**
 * Initial state
 */
const initialState = {
  mode: "own" as FilialeContextMode,
  selectedFilialeCodes: [] as string[],
  selectedDeptPath: null as string | null,
};

/**
 * Filiale context store with persistence
 * Persists to localStorage under STORAGE_KEYS.USER.FILIALE_CONTEXT
 */
export const useFilialeContextStore = create<FilialeContextStore>()(
  persist(
    immer((set) => ({
      ...initialState,

      setMode: (mode) =>
        set((state) => {
          state.mode = mode;
          if (mode === "own" || mode === "all") {
            state.selectedFilialeCodes = [];
          }
        }),

      setSelectedFilialeCodes: (codes) =>
        set((state) => {
          state.selectedFilialeCodes = [...codes];
          state.mode = codes.length > 0 ? "multi" : "own";
        }),

      setSelectedDeptPath: (path) =>
        set((state) => {
          state.selectedDeptPath = path;
        }),

      reset: () => set((state) => Object.assign(state, initialState)),
    })),
    {
      name: STORAGE_KEYS.USER.FILIALE_CONTEXT,
      // Only persist the raw selection, not derived state
      partialize: (state) => ({
        mode: state.mode,
        selectedFilialeCodes: state.selectedFilialeCodes,
        selectedDeptPath: state.selectedDeptPath,
      }),
    },
  ),
);

/**
 * Selector for the current mode
 */
export const selectMode = (state: FilialeContextStore): FilialeContextMode => state.mode;

/**
 * Selector for the selected filiale codes
 */
export const selectSelectedFilialeCodes = (state: FilialeContextStore): string[] =>
  state.selectedFilialeCodes;

/**
 * Selector for the selected department path
 */
export const selectSelectedDeptPath = (state: FilialeContextStore): string | null =>
  state.selectedDeptPath;
